import React, { useState, useCallback, useMemo } from "react";
import { View, Modal, Pressable, Image, Text, StyleSheet, Platform } from "react-native";
import { FontAwesomeIcon } from "../platform";
import { faEllipsisVertical } from "@fortawesome/free-solid-svg-icons";
import { COLORS } from "../theme/colors";

/**
 * SocialMediaDropdown Component
 *
 * Displays social media icons with an overflow dropdown menu.
 * Shows first 3 icons inline, remaining icons in a dropdown.
 * Uses native Modal component - no external dependencies.
 *
 * @param {Object} props - Component props
 * @param {Array<{name: string, label: string, asset: any, url: string}>} props.icons - Array of social media icon objects
 * @param {Function} props.onIconPress - Callback function when an icon is pressed, receives icon object
 * @returns {React.Component} Rendered component
 */
const SocialMediaDropdown = ({ icons, onIconPress }) => {
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
  const buttonRef = React.useRef(null);

  /** Memoized array of first 3 icons to display inline */
  const visibleIcons = useMemo(() => icons.slice(0, 3), [icons]);

  /** Memoized array of remaining icons to display in dropdown */
  const dropdownIcons = useMemo(() => icons.slice(3), [icons]);

  /**
   * Opens the dropdown menu and positions it below the three-dot button
   */
  const openDropdown = useCallback(() => {
    if (buttonRef.current) {
      try {
        buttonRef.current.measure((_x, _y, _width, height, _pageX, pageY) => {
          if (pageY !== undefined && height !== undefined) {
            setDropdownPosition({
              top: pageY + height,
              right: Platform.OS === "web" ? 15 : 10,
            });
            setDropdownVisible(true);
          }
        });
      } catch (error) {
        // Fallback: open dropdown with default positioning if measure fails
        setDropdownPosition({
          top: 60,
          right: Platform.OS === "web" ? 15 : 10,
        });
        setDropdownVisible(true);
      }
    }
  }, []);

  /**
   * Closes the dropdown menu
   */
  const closeDropdown = useCallback(() => {
    setDropdownVisible(false);
  }, []);

  /**
   * Handles press on a dropdown menu item
   * @param {Object} icon - The icon object that was pressed
   */
  const handleDropdownItemPress = useCallback(
    (icon) => {
      closeDropdown();
      onIconPress(icon);
    },
    [onIconPress, closeDropdown]
  );

  /**
   * Memoized dropdown menu styles with platform-specific positioning
   */
  const dropdownStyle = useMemo(
    () => [
      styles.dropdownMenu,
      Platform.OS === "web"
        ? { position: "absolute", top: dropdownPosition.top, right: dropdownPosition.right }
        : { marginTop: dropdownPosition.top, marginRight: dropdownPosition.right },
    ],
    [dropdownPosition.top, dropdownPosition.right]
  );

  return (
    <View style={styles.container}>
      {/* Render first 3 icons inline */}
      {visibleIcons.map((icon) => (
        <Pressable
          key={icon.name}
          onPress={() => onIconPress(icon)}
          accessibilityRole="link"
          accessibilityLabel={icon.label}
          style={styles.iconButton}
        >
          <Image source={icon.asset} style={styles.socialIcon} resizeMode="contain" />
        </Pressable>
      ))}

      {/* Three-dot menu button */}
      {dropdownIcons.length > 0 && (
        <Pressable
          ref={buttonRef}
          onPress={openDropdown}
          accessibilityLabel="More social media options"
          style={styles.moreButton}
        >
          <FontAwesomeIcon icon={faEllipsisVertical} size={20} color={COLORS.border} />
        </Pressable>
      )}

      {/* Dropdown Modal */}
      <Modal visible={dropdownVisible} transparent={true} animationType="fade" onRequestClose={closeDropdown}>
        <Pressable style={styles.modalOverlay} onPress={closeDropdown}>
          <View style={dropdownStyle} onStartShouldSetResponder={() => true}>
            {dropdownIcons.map((icon, index) => (
              <Pressable
                key={icon.name}
                onPress={() => handleDropdownItemPress(icon)}
                style={[styles.dropdownItem, index < dropdownIcons.length - 1 && styles.dropdownItemBorder]}
              >
                <Image source={icon.asset} style={styles.dropdownIcon} resizeMode="contain" />
                <Text style={styles.dropdownText}>{icon.label}</Text>
              </Pressable>
            ))}
          </View>
        </Pressable>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    marginRight: 15,
  },
  iconButton: {
    marginHorizontal: 5,
    padding: 2,
  },
  moreButton: {
    marginHorizontal: 5,
    padding: 5,
    justifyContent: "center",
    alignItems: "center",
  },
  socialIcon: {
    width: 32,
    height: 32,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.3)",
  },
  dropdownMenu: {
    backgroundColor: "white",
    borderRadius: 8,
    minWidth: 180,
    ...(Platform.OS === "web"
      ? { boxShadow: "0 2px 8px rgba(0, 0, 0, 0.25)" }
      : {
          shadowColor: "#000",
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.25,
          shadowRadius: 8,
          elevation: 5,
        }),
  },
  dropdownItem: {
    flexDirection: "row",
    alignItems: "center",
    padding: 12,
    paddingHorizontal: 16,
  },
  dropdownItemBorder: {
    borderBottomWidth: 1,
    borderBottomColor: COLORS.border + "30",
  },
  dropdownIcon: {
    width: 32,
    height: 32,
    marginRight: 12,
  },
  dropdownText: {
    fontSize: 15,
    color: COLORS.textDark,
  },
});

export default React.memo(SocialMediaDropdown);
